import { stringify } from "query-string";
import { SimpleRestServiceError } from "./SimpleRestServiceError";
import { CIQError, ErrorResponse, IRestClientErrorRequest } from "./models/api";

export async function call_get<T = Record<string, unknown>>(
  url: string,
  params?: any
) {
  const parsedUrl = new URL(url);
  parsedUrl.search = stringify(params);

  const response = await fetch(parsedUrl, {
    mode: "cors",
    credentials: "include"
  });

  if (!response.ok) {
    await handleError(response, {
      method: "GET",
      url: parsedUrl.toString(),
      params
    });
  }

  return (await response.json()) as T;
}

export async function call_post<
  RequestT = Record<string, unknown>,
  ResponseT = Record<string, unknown>
>(url: string, payload?: RequestT) {
  const requestOptions: RequestInit = {
    method: "POST",
    mode: "cors",
    credentials: "include"
  };

  const response = await fetch(new URL(url), {
    ...requestOptions,
    ...getRequestOptionsPayload(payload)
  });

  if (!response.ok) {
    await handleError(response, {
      method: "POST",
      url,
      payload
    });
  }

  return (await response.json()) as ResponseT;
}

export async function call_delete<ResponseT = any>(
  url: string,
  {
    params = {},
    payload = null
  }: { params?: Record<string, any>; payload?: Record<string, any> | null } = {}
): Promise<ResponseT> {
  const parsedUrl = new URL(url);
  parsedUrl.search = stringify(params);

  const requestOptions: RequestInit = {
    method: "DELETE",
    mode: "cors",
    credentials: "include"
  };

  const response = await fetch(parsedUrl, {
    ...requestOptions,
    ...getRequestOptionsPayload(payload)
  });

  if (!response.ok) {
    await handleError(response, {
      method: "DELETE",
      url,
      params,
      payload
    });
  }

  return (await response.json()) as ResponseT;
}

export async function call_put<
  RequestT = Record<string, unknown>,
  ResponseT = Record<string, unknown>
>(url: string, payload?: RequestT) {
  const requestOptions: RequestInit = {
    method: "PUT",
    mode: "cors",
    credentials: "include"
  };

  const response = await fetch(new URL(url), {
    ...requestOptions,
    ...getRequestOptionsPayload(payload)
  });

  if (!response.ok) {
    await handleError(response, {
      method: "PUT",
      url,
      payload
    });
  }

  return (await response.json()) as ResponseT;
}

function getRequestOptionsPayload<RequestT = Record<string, unknown>>(
  payload?: RequestT
) {
  const options: RequestInit = {};
  if (payload) {
    options.headers = {
      "Content-Type": "application/json"
    };
    options.body = JSON.stringify(payload);
  }
  return options;
}

async function handleError(
  response: Response,
  request: IRestClientErrorRequest
): Promise<never> {
  let errorData: ErrorResponse<CIQError>;

  try {
    errorData = await response.json();
  } catch {
    errorData = {
      message: "Unknown error",
      status: String(response.status),
      errors: []
    };
  }

  throw new SimpleRestServiceError(request, errorData);
}
